import * as React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { ApplicationState } from '../../store';
import { useMsal } from '@azure/msal-react';
import * as TeamsState from '../../store/TeamsState';
import { TokenHelper } from '../../TokenHelper';
import { ConvertionHelpers } from '../../ConvertionHelpers';
import { useTranslation } from 'react-i18next';
import { Consultant, ConsultantInvoice } from '../../interfaces/Consultant';
import { ConsultantActionCreators } from '../../store/consultants';
import { useEditorStyles, useStackClassName } from '../Styles/Editor';
import { Button, Dialog, DialogActions, DialogBody, DialogContent, DialogSurface, DialogTitle, Field, Input, InputOnChangeData, Label, Link, Switch, SwitchOnChangeData, Toast, ToastTitle, ToastTrigger, useToastController } from '@fluentui/react-components';
import { ConsultantDropdown } from '../EditorHelpers/ConsultantDropdown';
import { DatePicker } from '@fluentui/react-datepicker-compat';
import { onFormatDate } from '../Tools/converters';
import { VATDropdown } from '../EditorHelpers/VATDropdown';
import { PeriodeDropdown } from '../EditorHelpers/PeriodeDropdown';
import { FileViewer } from '../FileViewer/FileViewer';
import { CurrencyInput } from '../EditorHelpers/CurrencyInput';
import { DownloadFile } from '../Tools/FileDownloadHelper';
import { ChangeEvent } from 'react';
import { useConsultants, useRigths } from '../Tools/ObjectLoader';

export const ConsultantsInvoicesEditor = () => {
    // All data needed for edition should be loaded before calling the editor
    const teamsState = useSelector<ApplicationState, TeamsState.TeamsState>((state: ApplicationState) => state.teamsState);
    const { isAdmin } = useRigths();
    const consultantInvoiceEdit = useSelector<ApplicationState, ConsultantInvoice | null>((state: ApplicationState) => state.consultantsState.consultantInvoiceEdit);
    const { consultants } = useConsultants();
    const consultantsInvoices = useSelector<ApplicationState, ConsultantInvoice[] | undefined>((state: ApplicationState) => state.consultantsState.consultantsInvoices);
    // Gen hooks
    const dispatch = useDispatch();
    const { instance } = useMsal();
    const { t } = useTranslation();
    const styles = useEditorStyles();
    const { dispatchToast, updateToast } = useToastController("NeoToasterUnique");
    // Fields
    const [invoiceRef, setInvoiceRef] = React.useState(consultantInvoiceEdit ? consultantInvoiceEdit.invoiceRef : "");
    const [periode, setPeriode] = React.useState<Date | null>(consultantInvoiceEdit && consultantInvoiceEdit.periode ? new Date(consultantInvoiceEdit.periode) : null);
    const [paymentDate, setPaymentDate] = React.useState<Date | null>(consultantInvoiceEdit && consultantInvoiceEdit.paymentDate ? new Date(consultantInvoiceEdit.paymentDate) : null);
    const [amount, setAmount] = React.useState<number>(consultantInvoiceEdit && consultantInvoiceEdit.amount ? consultantInvoiceEdit.amount : 0);
    const [description, setDescription] = React.useState<string | null>(consultantInvoiceEdit && consultantInvoiceEdit.description);
    const [lock, setLock] = React.useState<boolean>(consultantInvoiceEdit ? consultantInvoiceEdit.lock : false);
    const [vatCode, setVatCode] = React.useState<number>(consultantInvoiceEdit && consultantInvoiceEdit.vatcode !== null ? consultantInvoiceEdit.vatcode : 21);
    const [accountingRef, setAccountingRef] = React.useState<string | null>(consultantInvoiceEdit ? consultantInvoiceEdit.accountingRef : "");
    const [documentDate, setDocumentDate] = React.useState<Date>(consultantInvoiceEdit && consultantInvoiceEdit.documentDate ? new Date(consultantInvoiceEdit.documentDate) : new Date());
    // Fields validation
    const [isInvoiceRefValid, setInvoiceRefValid] = React.useState(false);
    const [isInvoiceRefAllReadyExist, setInvoiceRefAllReadyExist] = React.useState(false);
    // Form validation
    const [isSubmitValid, setSubmitValid] = React.useState(false);
    // Dependencies
    const [consultant, setConsultant] = React.useState<Consultant | null | undefined>(consultants && consultantInvoiceEdit ? consultants.find((c) => consultantInvoiceEdit && c.consultantid === consultantInvoiceEdit.consultantid) : undefined);
    const isEditAndFile = React.useMemo(() => { return (consultantInvoiceEdit && consultantInvoiceEdit.consultantInvoiceid !== null && consultantInvoiceEdit.sharePointFileUniqueId) ?? false; }, [consultantInvoiceEdit]);
    const [file, setFile] = React.useState<Blob>();

    React.useEffect(() => {
        if (isEditAndFile)
            TokenHelper.GetToken(instance, teamsState)
                .then(token => {
                    if (token !== null && consultantInvoiceEdit && consultantInvoiceEdit.consultantInvoiceid) {
                        DownloadFile('api/ConsultantsInvoicesFile/' + consultantInvoiceEdit.consultantInvoiceid, token)
                            .then((f) => {
                                if (f) {
                                    setFile(f);
                                }
                            })
                            .catch(() => { });
                    }
                });
    }, [isEditAndFile]);

    React.useEffect(() => {
        const b: boolean = invoiceRef !== undefined && invoiceRef !== null && invoiceRef.length > 0;
        if (b && consultantsInvoices) {
            if (isAdmin) {
                // For Admins, we cn do what we want ;-)
                setInvoiceRefAllReadyExist(false);
            }
            else {
                // For consultant, since an invoice ref must be unique, check
                const i = consultantsInvoices.findIndex(i => i.invoiceRef === invoiceRef);
                setInvoiceRefAllReadyExist(i !== -1);
            }
        } else {
            setInvoiceRefAllReadyExist(false);
        }
        setInvoiceRefValid(b);
    }, [invoiceRef, isAdmin]);
    React.useEffect(() => {
        const submitValid =
            isInvoiceRefValid
            && !isInvoiceRefAllReadyExist
            && periode !== null
            && amount > 0;
        //if (submitValid && data && data.customerEdit) {
        //    // If valid, check if any change on existing item
        //    submitValid = submitValid && (
        //        displayName !== data.customerEdit.displayName
        //        || name !== data.customerEdit.name
        //        || vat !== data.customerEdit.vat
        //        || address != data.customerEdit.address
        //        || postalCode != data.customerEdit.postalCode
        //        || city != data.customerEdit.city
        //        || country != data.customerEdit.country
        //    );
        //}
        setSubmitValid(submitValid);
    }, [isInvoiceRefValid, isInvoiceRefAllReadyExist, periode, amount]);

    const submit = () => {
        const c: ConsultantInvoice = {
            consultantInvoiceid: consultantInvoiceEdit && consultantInvoiceEdit.consultantInvoiceid ? consultantInvoiceEdit.consultantInvoiceid : null,
            consultantid: consultant ? consultant.consultantid : null,
            invoiceRef: invoiceRef,
            creationDate: null,
            periode: ConvertionHelpers.DateToISODateOnly(periode),
            sharePointFileUniqueId: null,
            invoiceFileExist: false,
            paymentDate: ConvertionHelpers.DateToISODateOnly(paymentDate),
            amount: amount,
            description: description,
            lock: lock,
            vatcode: vatCode,
            documentDate: ConvertionHelpers.DateToISODateOnly(documentDate) ?? "",
            accountingRef: accountingRef,
        };

        TokenHelper.GetToken(instance, teamsState)
            .then(token => {
                const toastId = (Math.random() + 1).toString(36).substring(7);
                dispatchToast(
                    <Toast appearance="inverted">
                        <ToastTitle>
                            {"Saving invoice " + c.invoiceRef + "..."}
                        </ToastTitle>
                    </Toast>,
                    { intent: "info", timeout: -1, position: "top-end", toastId: toastId }
                );
                if (token !== null) {
                    fetch("api/ConsultantsInvoices/" + (consultantInvoiceEdit && consultantInvoiceEdit.consultantInvoiceid !== null ? consultantInvoiceEdit.consultantInvoiceid : ""), {
                        method: consultantInvoiceEdit && consultantInvoiceEdit.consultantInvoiceid === null ? "POST" : "PUT",  // if customerid exist, path
                        headers: {
                            "Content-Type": "application/json",
                            authorization: "bearer " + token,
                        },
                        body: JSON.stringify(c),
                        mode: "cors",
                        cache: "default",
                    })
                        .then(response => {
                            if (response.status === 200) {
                                updateToast({
                                    content: (
                                        <Toast appearance="inverted">
                                            <ToastTitle action={
                                                <ToastTrigger>
                                                    <Link>Dismiss</Link>
                                                </ToastTrigger>
                                            }>
                                                {c.invoiceRef ? ("Invoice " + c.invoiceRef + " saved") : "Invoice saved"}
                                            </ToastTitle>
                                        </Toast>
                                    ),
                                    intent: "success",
                                    toastId,
                                    timeout: 5000,
                                });
                                return response.json() as Promise<ConsultantInvoice>;
                            }
                            else {
                                updateToast({
                                    content: (
                                        <Toast appearance="inverted">
                                            <ToastTitle action={
                                                <ToastTrigger>
                                                    <Link>Dismiss</Link>
                                                </ToastTrigger>
                                            }>
                                                c.invoiceRef ? ("Invoice " + c.invoiceRef + " save failed") : "Invoice save failed"
                                            </ToastTitle>
                                        </Toast>
                                    ),
                                    intent: "error",
                                    toastId,
                                    timeout: 10000,
                                });
                            }
                        })
                        .then(data => {
                            if (data) {
                                dispatch(ConsultantActionCreators.patchConsultantInvoiceEdition(data));
                                dispatch(ConsultantActionCreators.requestConsultantInvoiceEdition(null));
                            }
                        });
                }
            });
    };

    return <Dialog
        open
        modalType="modal">
        <DialogSurface className={isEditAndFile ? styles.DialogContentWithFilePreview : undefined}>
            <DialogBody className={isEditAndFile ? styles.fluid : undefined}>
                <DialogTitle>{consultantInvoiceEdit && consultantInvoiceEdit.consultantInvoiceid !== null ? "Edit invoice " + consultantInvoiceEdit.invoiceRef : "Create new invoice"}</DialogTitle>
                <DialogContent>
                    <div className={styles.onSingleLinWithFilePreviewe}>
                        {isEditAndFile &&
                            <FileViewer file={file} />
                        }
                        <div className={isEditAndFile ? styles.InfoPart : styles.fluid}>
                            <form
                                onSubmit={() => {
                                    dispatch(ConsultantActionCreators.requestConsultantInvoiceEdition(null));
                                }}
                                className={useStackClassName()}
                                autoComplete="off"
                            >
                                {isAdmin &&
                                    <div className={styles.onSingleLine}>
                                        <div className={styles.field50}>
                                            <Field label={t('Consultant')} className={styles.fluid} required>
                                                <ConsultantDropdown
                                                    currentConsultant={consultant ?? undefined}
                                                    setConsultant={setConsultant}
                                                    ariaInvalid={!consultant}
                                                />
                                            </Field>
                                        </div>
                                        <div className={styles.field50}>
                                            <Field label={t('DateOfSubmission')} className={styles.fluid}>
                                                <Label>{consultantInvoiceEdit && consultantInvoiceEdit.creationDate ? new Date(consultantInvoiceEdit.creationDate + "Z").toLocaleString() : "-"}</Label>
                                            </Field>
                                        </div>
                                    </div>
                                }
                                <Field label={t('InvoiceRef')}
                                    required
                                >
                                    <Input
                                        autoComplete="off"
                                        className={styles.fluid}
                                        onChange={(e: ChangeEvent<HTMLInputElement>, data: InputOnChangeData) => setInvoiceRef(data.value)}
                                        value={invoiceRef !== null ? invoiceRef : ""}
                                        aria-invalid={!isInvoiceRefValid}
                                        maxLength={50}
                                    />
                                </Field>
                                {isAdmin
                                    ?
                                    <div className={styles.onSingleLine}>
                                        <div className={styles.field50}>
                                            <Field label={t('Periode')} className={styles.fluid} required>
                                                <PeriodeDropdown currentPeriode={periode} setPeriode={setPeriode} />
                                            </Field>
                                        </div>
                                        <div className={styles.field50}>
                                            <Field label="Date" className={styles.fluid}
                                                required
                                            >
                                                <DatePicker className={styles.fluid}
                                                    onSelectDate={(date: Date | null | undefined) => { setPeriode(date === undefined ? null : date); }}
                                                    value={periode === null ? undefined : periode}
                                                    formatDate={onFormatDate}
                                                    placeholder={t('SelectInvoiceDate')}
                                                    aria-invalid={periode === null}
                                                />
                                            </Field>
                                        </div>
                                    </div>
                                    :
                                    <Field label="Periode" className={styles.fluid} required>
                                        <PeriodeDropdown currentPeriode={periode} setPeriode={setPeriode} />
                                    </Field>
                                }
                                <Field label={t('InvoiceDate')}
                                    className={styles.fluid}
                                    required
                                >
                                    <DatePicker className={styles.fluid}
                                        onSelectDate={(date: Date | null | undefined) => { setDocumentDate(date === undefined || date === null ? new Date() : date); }}
                                        value={documentDate}
                                        formatDate={onFormatDate}
                                        placeholder={t('SelectInvoiceDate')}
                                        aria-invalid={documentDate === null}
                                        minDate={new Date(2024, 0, 1)}
                                        maxDate={new Date()}
                                    />
                                </Field>
                                <Field label={t('Billed') + " (" + t('ExcludVAT') + ")"}
                                    className={styles.fluid}
                                    required
                                >
                                    <CurrencyInput
                                        setCurrency={setAmount}
                                        currency={amount}
                                        digits={2}
                                    />
                                </Field>

                                <Field label={t('VAT')} className={styles.fluid}>
                                    <VATDropdown currentVAT={vatCode} setVatCode={setVatCode} />
                                </Field>
                                {isAdmin && <>
                                    <Field label="Payment Date" className={styles.fluid}>
                                        <DatePicker className={styles.fluid}
                                            onSelectDate={(date: Date | null | undefined) => { setPaymentDate(date === undefined ? null : date); }}
                                            value={paymentDate === null ? undefined : paymentDate}
                                            formatDate={onFormatDate}
                                            placeholder={t('SelectPaymentDate')}
                                        />
                                    </Field>
                                    <Field label={t('AccountingRef')} >
                                        <Input
                                            autoComplete="off"
                                            className={styles.fluid}
                                            onChange={(e: ChangeEvent<HTMLInputElement>, data: InputOnChangeData) => setAccountingRef(data.value)}
                                            value={accountingRef !== null ? accountingRef : ""}
                                        />
                                    </Field>
                                </>
                                }
                                {isAdmin &&
                                    <Switch label="Lock"
                                        checked={lock}
                                        onChange={(ev: React.ChangeEvent<HTMLInputElement>, data: SwitchOnChangeData) => { setLock(data.checked); }}
                                    />}
                            </form>
                        </div>
                    </div>
                </DialogContent>
                <DialogActions>
                    <Button
                        onClick={() => dispatch(ConsultantActionCreators.requestConsultantInvoiceEdition(null))}
                    >
                        {t("Cancel")}
                    </Button>
                    <Button
                        onClick={() => submit()}
                        type="submit"
                        form="my-form-id"
                        appearance="primary"
                        disabled={!isSubmitValid}
                    >
                        {t("Save")}
                    </Button>
                </DialogActions>
            </DialogBody>
        </DialogSurface>
    </Dialog>;
};